import { Component, inject, computed, signal, AfterViewInit, viewChild, resource, ChangeDetectionStrategy } from '@angular/core';
import { Router } from '@angular/router';
import { StatusTypeService } from './services/status-type.service';
import { StakeholderService } from '../stakeholder/services/stakeholder.service';
import { PartnerSelectorComponent } from '../partner/partner-selector.component';
import { DocumentTypeEntity, DistributionObjectGroup, DocumentConfig } from '../distribution/models';
import { DistributionObjectSelectorComponent } from '../distribution/distribution-object-selector.component';
import { DocumentTypeSelectorComponent } from '../distribution/document-type-selector/document-type-selector.component';
import { TrackingFieldService } from './services/tracking-field.service';
import { DataFieldGroup } from '../shared/datafields/models/datafield-group';
import { DataFieldGroupContainerComponent } from '../shared/datafields/datafield-group-container.component';
import { DateTimeService } from '../shared/datetime/datetime.service';
import { DatetimePickerComponent } from '../shared/datetime-picker/datetime-picker.component';
import { LoadingPanelService, LoadingPanelSettings } from '../shared/loading-panel/loading-panel.service';
import { StateService } from '../shared/state-management/state.service';
import { Guid } from '../shared/utils';
import { PartnerSystemService } from '../partner/services/partner-system.service';
import { FormsModule } from '@angular/forms';
import { AutoFocusDirective } from '../shared/auto-focus/auto-focus.directive';
import { SvgIconComponent } from '../shared/icons/svg-icon.component';
import { DataFieldTypes } from 'src/app/shared/datafields/models/datafield-type';
import { DataField } from 'src/app/shared/datafields/models/datafield';
import { DxAccordionModule, DxAccordionComponent, DxCheckBoxModule, DxSliderModule, DxTagBoxModule } from 'devextreme-angular';
import { firstValueFrom } from 'rxjs';

@Component({
    templateUrl: './tracking-search.component.html',
    styleUrls: ['./tracking-search.component.scss'],
    changeDetection: ChangeDetectionStrategy.Eager,
    imports: [
        FormsModule,
        AutoFocusDirective,
        DistributionObjectSelectorComponent,
        DatetimePickerComponent,
        PartnerSelectorComponent,
        DocumentTypeSelectorComponent,
        DataFieldGroupContainerComponent,
        SvgIconComponent,
        DxAccordionModule,
        DxCheckBoxModule,
        DxSliderModule,
        DxTagBoxModule,
    ],
})
export class TrackingSearchComponent implements AfterViewInit {
    private readonly statusTypeService = inject(StatusTypeService);
    private readonly stakeholderService = inject(StakeholderService);
    private readonly partnerSystemService = inject(PartnerSystemService);
    private readonly router = inject(Router);
    private readonly trackingFieldService = inject(TrackingFieldService);
    private readonly loadingPanelService = inject(LoadingPanelService);
    private readonly stateService = inject(StateService);
    private readonly dateTimeService = inject(DateTimeService);

    private readonly accordion = viewChild<DxAccordionComponent>('accordion');
    private readonly docTypeIn = viewChild<DocumentTypeSelectorComponent>('docTypeIn');
    private readonly docTypeOut = viewChild<DocumentTypeSelectorComponent>('docTypeOut');
    private readonly partnerSelectorFrom = viewChild<PartnerSelectorComponent>('partnerSelectorFrom');
    private readonly partnerSelectorTo = viewChild<PartnerSelectorComponent>('partnerSelectorTo');
    private readonly trackingFieldContainer = viewChild<DataFieldGroupContainerComponent>('trackingFieldContainer');
    private readonly distributionObjectSelector = viewChild<DistributionObjectSelectorComponent>('distributionObjectSelector');
    private readonly datepickerFrom = viewChild<DatetimePickerComponent>('datepickerFrom');
    private readonly datepickerTo = viewChild<DatetimePickerComponent>('datepickerTo');

    readonly stateKey: string = 'tracking-search-state';
    searchObject: TrackingSearchModel = this.createDefaultSearchModel();

    readonly statusTypesResource = resource({
        params: () => null,
        loader: () => this.statusTypeService.getStatusTypes(),
    });

    readonly stakeholdersResource = resource({
        params: () => null,
        loader: () => this.stakeholderService.getStakeholders(),
    });

    readonly partnerSystemsResource = resource({
        params: () => null,
        loader: () => this.partnerSystemService.getAllPartnerSystems(),
    });

    private readonly openedSectionKeys = signal<readonly SectionKey[]>([]);
    private readonly autoExpandEnabled = signal<boolean>(false);
    trackingFieldGroups = signal<DataFieldGroup[]>([]);
    isLoadingFromState = signal<boolean>(false);
    errorSectionCount = signal<number>(0);
    partnerSectionCount = signal<number>(0);
    advancedSectionCount = signal<number>(0);
    trackingFieldsSectionCount = signal<number>(0);

    readonly partnerSystems = computed(() => {
        const partnerSystems = this.partnerSystemsResource.value() ?? [];
        return [...partnerSystems].sort((a, b) => a.Name.localeCompare(b.Name));
    });

    readonly statusTypes = computed(() => {
        const statusTypes = this.statusTypesResource.value() ?? [];
        return [...statusTypes].sort((a, b) => a.StatusTypeName.localeCompare(b.StatusTypeName));
    });

    readonly stakeholders = computed(() => {
        const stakeholders = this.stakeholdersResource.value() ?? [];
        return [...stakeholders].sort((a, b) => a.Name.localeCompare(b.Name));
    });

    readonly isTrackingFieldsVisible = computed<boolean>(() => {
        const groups = this.trackingFieldGroups();
        return Array.isArray(groups) && groups.length > 0;
    });

    ngAfterViewInit(): void {
        this.loadingPanelService.showLoadingPanel(new LoadingPanelSettings('Initialize data...'));
        void this.initializeAsync();
        this.applyAutoOpenByCounts();
    }

    search(): void {

        this.runWithAutoExpand((): void => {
            this.updateSectionCounts();
        });

        const tempSearchObject: Partial<TrackingSearchModel> = {};
        for (const key of Object.keys(this.searchObject) as (keyof TrackingSearchModel)[]) {
            const value = this.searchObject[key];

            const isEmptyString = typeof value === 'string' && value.trim() === '';
            if (value === null || value === undefined || isEmptyString) {
                continue;
            }

            if (this.isRecord(value)) {
                continue;
            }

            this.setTemp(tempSearchObject, key, value);
        }

        const trackingFieldValues = this.trackingFieldContainer()?.getDataFieldValues(false) ?? [];
        if (trackingFieldValues.length > 0) {
            tempSearchObject.trackingFields =
                this.trackingFieldService.generateTrackingFieldSearchParameters(trackingFieldValues);
        } else {
            delete tempSearchObject.trackingFields;
        }

        tempSearchObject.searchId = Guid.newGuid();

        const stateData: TrackingSearchState = {
            searchObject: tempSearchObject,
            trackingFields: this.trackingFieldContainer()?.datafieldGroups ?? this.trackingFieldGroups() ?? [],
            openedSectionKeys: this.openedSectionKeys(),
        };

        this.stateService.setState(this.stateKey, stateData);
        void this.router.navigate(['tracking/result', tempSearchObject]);
    }

    reset(resetState: boolean = true): void {
        if (resetState) {
            this.isLoadingFromState.set(false);
            this.stateService.removeState(this.stateKey);
        }

        this.initializeSearchObject();
        this.updateSectionCounts();

        void this.docTypeIn()?.reset();
        void this.docTypeOut()?.reset();
        this.partnerSelectorFrom()?.reset();
        this.partnerSelectorTo()?.reset();
        this.distributionObjectSelector()?.reset();
        this.datepickerFrom()?.reset();
        this.datepickerTo()?.reset();

        this.openedSectionKeys.set([]);
        this.syncAccordionExpandedStateFromKeys([]);
        this.persistOpenSections();
        this.openSection('partner');
    }

    distributionObjectChanged(distributionObject: DocumentTypeEntity): void {
        if (this.isLoadingFromState()) {
            return;
        }

        void this.docTypeIn()?.refresh(distributionObject ? distributionObject.Id : null);
        void this.docTypeOut()?.refresh(distributionObject ? distributionObject.Id : null);
        this.searchObject.distributionObjectId = distributionObject ? distributionObject.Id : null;

        if (distributionObject?.Id) {
            void this.trackingFieldService
                .getGroupedDataFieldsForDistributionObject(distributionObject.Id)
                .then((datafieldGroups) => {
                    this.trackingFieldGroups.set(datafieldGroups);
                    this.openSection('trackingFields');
                });
        } else {
            this.runWithAutoExpand((): void => {
                this.trackingFieldGroups.set([]);
                this.updateSectionCounts();
            });
        }
        this.openSection('partner');
    }

    distributionObjectGroupChanged(distributionObjectGroup: DistributionObjectGroup): void {
        this.searchObject.distributionObjectGroupId = distributionObjectGroup?.DistributionObjectGroupId ?? null;
    }

    onAccordionSelectionChanged(e: AccordionSelectionChangedEvent): void {
        const keys = this.getOpenedKeysFromAccordionEvent(e);
        this.openedSectionKeys.set(keys);
        this.persistOpenSections();
    }

    setTimeSpan(type: string): void {
        const tempDate = new Date();
        const dateTo = new Date(tempDate.getFullYear(), tempDate.getMonth(), tempDate.getDate());
        const dateFrom = new Date(tempDate.getFullYear(), tempDate.getMonth(), tempDate.getDate());
        const period = this.dateTimeService.setTimeSpan(type, dateTo, dateFrom);

        this.dateFromChanged(period.start);
        this.dateToChanged(period.end);

        this.datepickerFrom()?.setSelectedDate(period.start);
        this.datepickerTo()?.setSelectedDate(period.end);
    }

    updateSectionCounts(): void {
        const sectionCounts = this.getSectionValueCounts(this.searchObject, this.trackingFieldGroups());
        this.errorSectionCount.set(sectionCounts.error);
        this.partnerSectionCount.set(sectionCounts.partner);
        this.advancedSectionCount.set(sectionCounts.advanced);
        this.trackingFieldsSectionCount.set(sectionCounts.trackingFields);

        if (this.autoExpandEnabled()) {
            this.applyAutoOpenByCounts();
        }
    }

    setStateData(): boolean {

        const stateData = this.stateService.getState(this.stateKey) as TrackingSearchState | null;

        if (!stateData) {
            return false;
        }

        this.isLoadingFromState.set(true);

        this.initializeSearchObject();
        Object.assign(this.searchObject, stateData.searchObject);

        if (this.searchObject.partnerIdFrom) {
            this.partnerSelectorFrom().setSelectedPartnerId(this.searchObject.partnerIdFrom);
        }
        if (this.searchObject.partnerIdTo) {
            this.partnerSelectorTo().setSelectedPartnerId(this.searchObject.partnerIdTo);
        }
        if (stateData.trackingFields) {
            this.trackingFieldGroups.set(stateData.trackingFields);
        }

        let docInPromise: Promise<void> | undefined;
        let docOutPromise: Promise<void> | undefined;

        if (this.searchObject.distributionObjectId) {
            this.distributionObjectSelector().setSelectedDistributionObjectId(this.searchObject.distributionObjectId);
            docInPromise = this.docTypeIn().refresh(this.searchObject.distributionObjectId);
            docOutPromise = this.docTypeOut().refresh(this.searchObject.distributionObjectId);
        }

        this.distributionObjectSelector().setSelectedDistributionObjectGroupId(
            this.searchObject.distributionObjectGroupId,
        );

        if (this.searchObject.dateFrom) {
            this.datepickerFrom().setSelectedDate(new Date(this.searchObject.dateFrom));
        }
        if (this.searchObject.dateTo) {
            this.datepickerTo().setSelectedDate(new Date(this.searchObject.dateTo));
        }

        if (this.searchObject.documentTypeIdFrom) {
            const executor = () => this.docTypeIn().setSelected(this.searchObject.documentTypeIdFrom);
            if (docInPromise != null) {
                docInPromise.then(executor);
            } else {
                executor();
            }
        }

        if (this.searchObject.documentTypeIdTo) {
            const executor = () => this.docTypeOut().setSelected(this.searchObject.documentTypeIdTo);
            if (docOutPromise != null) {
                docOutPromise.then(executor);
            } else {
                executor();
            }
        }

        this.runWithAutoExpand((): void => {
            this.updateSectionCounts();
        });

        this.isLoadingFromState.set(false);
        return true;
    }

    getSectionValueCounts(
        searchObject?: Partial<TrackingSearchModel>,
        trackingFields?: DataFieldGroup[],
    ): SectionValueCounts {
        return {
            error: this.getSectionValueCount(searchObject, SECTION_KEYS.error),
            partner: this.getSectionValueCount(searchObject, SECTION_KEYS.partner),
            advanced: this.getSectionValueCount(searchObject, SECTION_KEYS.advanced),
            trackingFields: this.getTrackingFieldsValueCount(trackingFields),
        };
    }

    getAccordionCount(titleOrKey: string | null | undefined): number {
        const key = this.coerceSectionKey(titleOrKey);
        return key ? this.getSectionCount(key) : 0;
    }

    isSectionClosed(title: string) : boolean {
        const key = SECTION_KEY_BY_TITLE.get(title);
        return key ? !this.openedSectionKeys().includes(key) : true;
    }

    dateFromChanged(date: Date): void {
        this.searchObject.dateFrom = date ? date.toISOString() : null;
    }

    dateToChanged(date: Date): void {
        this.searchObject.dateTo = date ? date.toISOString() : null;
    }

    initializeSearchObject(): void {
        this.searchObject = this.createDefaultSearchModel();
    }

    trimFilename(): void {
        if (this.searchObject.filename) {
            this.searchObject.filename = this.searchObject.filename.trim();
        }
    }

    documentTypeInChanged(documentConfig: DocumentConfig): void {
        this.searchObject.formatIdFrom = documentConfig ? documentConfig.Format.FormatsId : undefined;
        this.searchObject.variantIdFrom = documentConfig ? documentConfig.Variant.VariantsId : undefined;
        this.searchObject.versionIdFrom = documentConfig ? documentConfig.VariantVersion.VariantVersionsId : undefined;
        this.searchObject.documentTypeIdFrom = documentConfig ? documentConfig.DocumentConfigsId : undefined;
        this.updateSectionCounts();
    }

    documentTypeOutChanged(documentConfig: DocumentConfig): void {
        this.searchObject.formatIdTo = documentConfig ? documentConfig.Format.FormatsId : undefined;
        this.searchObject.variantIdTo = documentConfig ? documentConfig.Variant.VariantsId : undefined;
        this.searchObject.versionIdTo = documentConfig ? documentConfig.VariantVersion.VariantVersionsId : undefined;
        this.searchObject.documentTypeIdTo = documentConfig ? documentConfig.DocumentConfigsId : undefined;
        this.updateSectionCounts();
    }

    private runWithAutoExpand(fn: () => void): void {
        this.autoExpandEnabled.set(true);
        try {
            fn();
        } finally {
            this.autoExpandEnabled.set(false);
        }
    }
    private async initializeAsync(): Promise<void> {
        let restoredFromState = false;

        try {
            const initPromises: Promise<unknown>[] = [];

            const distSelector = this.distributionObjectSelector();
            if (distSelector?.initialized) {
                initPromises.push(firstValueFrom(distSelector.initialized, { defaultValue: null }));
            }

            const docIn = this.docTypeIn();
            if (docIn?.initialized) {
                initPromises.push(firstValueFrom(docIn.initialized, { defaultValue: null }));
            }

            const docOut = this.docTypeOut();
            if (docOut?.initialized) {
                initPromises.push(firstValueFrom(docOut.initialized, { defaultValue: null }));
            }

            if (initPromises.length > 0) {
                await Promise.all(initPromises);
            }

            restoredFromState = this.setStateData();
            if (!restoredFromState) {
                this.initializeSearchObject();
            }

            this.updateSectionCounts();

        } finally {
            this.loadingPanelService.hideLoadingPanel();
        }
    }

    private openSection(key: SectionKey): void {
        const current = this.openedSectionKeys();
        const next = current.includes(key) ? current : [...current, key];

        this.openedSectionKeys.set(next);
        this.syncAccordionExpandedStateFromKeys(next);
        this.persistOpenSections();
    }

    private isRecord(value: unknown): value is Record<string, unknown> {
        return typeof value === 'object' && value !== null;
    }

    private persistOpenSections(): void {
        const existing = this.stateService.getState(this.stateKey) as TrackingSearchState | null;

        const next: TrackingSearchState = {
            searchObject: existing?.searchObject ?? {},
            trackingFields: existing?.trackingFields ?? this.trackingFieldGroups() ?? [],
            openedSectionKeys: this.openedSectionKeys(),
        };

        this.stateService.setState(this.stateKey, next);
    }

    private getSectionValueCount(
        source: Partial<TrackingSearchModel> | undefined,
        keys: readonly (keyof TrackingSearchModel)[],
    ): number {
        if (!source) {
            return 0;
        }

        let count = 0;
        for (const k of keys) {
            const v = source[k];
            if (v === null || v === undefined) {
                continue;
            }
            if (typeof v === 'string') {
                if (v.trim() === '') {
                    continue;
                }
            }

            count++;
        }
        return count;
    }

    private getTrackingFieldsValueCount(trackingFields?: DataFieldGroup[]): number {
        const fields: DataField[] = trackingFields ? DataFieldGroup.getDataFields(trackingFields) : [];
        return fields.reduce((sum, df) => sum + (this.isTrackingFieldFilled(df) ? 1 : 0), 0);
    }

    private isTrackingFieldFilled(df: DataField): boolean {
        if (!df || df.IsHidden) {
            return false;
        }

        const type = df.DataType?.DataTypeEnum;
        const v = df.Value;

        if (v === null || v === undefined) {
            return false;
        }

        switch (type) {
            case DataFieldTypes.CheckBox: {
                if (typeof v === 'boolean') {
                    return v;
                }
                if (typeof v === 'number') {
                    return v === 1;
                }

                const s = String(v).trim().toLowerCase();
                return s === 'true' || s === '1';
            }

            case DataFieldTypes.DropDown: {
                const s = v.toString().trim();
                return s !== '' && s.toLowerCase() !== 'null';
            }

            default:
                return v.toString().trim() !== '';
        }
    }

    private createDefaultSearchModel(): TrackingSearchModel {
        return {
            stakeholderUserGroupId: null,
            statusTypeId: null,
            partnerSystemId: null,
        };
    }

    private setTemp<K extends keyof TrackingSearchModel>(
        target: Partial<TrackingSearchModel>,
        key: K,
        value: TrackingSearchModel[K],
    ): void {
        target[key] = value;
    }

    private coerceSectionKey(value: string | null | undefined): SectionKey | null {
        if (value === null) {
            return null;
        }

        if (value === 'error' || value === 'partner' || value === 'advanced' || value === 'trackingFields') {
            return value;
        }

        return SECTION_KEY_BY_TITLE.get(value) ?? null;
    }

    private getSectionCount(key: SectionKey): number {
        const countsByKey: Record<SectionKey, () => number> = {
            error: () => this.errorSectionCount(),
            partner: () => this.partnerSectionCount(),
            advanced: () => this.advancedSectionCount(),
            trackingFields: () => this.trackingFieldsSectionCount(),
        };

        return countsByKey[key]();
    }

    private getNormalizedOpenedKeys(keys: readonly SectionKey[]): readonly SectionKey[] {
        return keys.filter((k) => k !== 'trackingFields' || this.isTrackingFieldsVisible());
    }

    private applyAutoOpenByCounts(): void {
        const keysToOpen: SectionKey[] = [];

        for (const key of NON_TRACKING_KEYS) {
            if (this.getSectionCount(key) > 0 || key === 'partner') {
                keysToOpen.push(key);
            }
        }

        if (this.isTrackingFieldsVisible() && this.getSectionCount('trackingFields') > 0) {
            keysToOpen.push('trackingFields');
        }

        const normalized = this.getNormalizedOpenedKeys(keysToOpen);
        this.openedSectionKeys.set(normalized);
        this.syncAccordionExpandedStateFromKeys(normalized);
        this.persistOpenSections();
    }

    private syncAccordionExpandedStateFromKeys(keys: readonly SectionKey[]): void {
        const accordionInstance = this.accordion()?.instance;
        if (!accordionInstance) {
            return;
        }

        const itemsUnknown = accordionInstance.option('items') as unknown;
        const items: readonly AccordionItem[] = Array.isArray(itemsUnknown) ? (itemsUnknown as AccordionItem[]) : [];

        const normalizedKeys = this.getNormalizedOpenedKeys(keys);
        const wantedTitles = new Set(
            normalizedKeys
                .map((k) => SECTION_TITLE_BY_KEY.get(k))
                .filter((t): t is string => typeof t === 'string'),
        );

        const selectedItems: AccordionItem[] = [];
        for (const item of items) {
            const title = this.tryGetItemTitle(item);
            if (title && wantedTitles.has(title)) {
                selectedItems.push(item);
            }
        }

        accordionInstance.option('selectedItems', selectedItems);
    }

    private getOpenedKeysFromAccordionEvent(e: AccordionSelectionChangedEvent): readonly SectionKey[] {
        const selectedUnknown = e.component?.option('selectedItems');
        if (!Array.isArray(selectedUnknown)) {
            return [];
        }

        const seen = new Set<SectionKey>();
        const next: SectionKey[] = [];

        for (const item of selectedUnknown) {
            const title = this.tryGetItemTitle(item);
            const key = title ? (SECTION_KEY_BY_TITLE.get(title) ?? null) : null;

            if (!key) {
                continue;
            }

            if (key === 'trackingFields' && !this.isTrackingFieldsVisible()) {
                continue;
            }

            if (!seen.has(key)) {
                seen.add(key);
                next.push(key);
            }
        }

        return next;
    }

    private tryGetItemTitle(item: unknown): string | null {
        if (!this.isRecord(item)) {
            return null;
        }

        const v = item['title'];
        return typeof v === 'string' ? v : null;
    }
}

interface BaseSearchFields {
    documentNumber?: string;
    statusTypeId: number | null;
    distributionObjectId?: number | null;
    distributionObjectGroupId?: number | null;
    dateFrom?: string | null;
    dateTo?: string | null;
    documentTypeIdFrom?: number;
    documentTypeIdTo?: number;
    searchId?: string;
    trackingFields?: string;
}

interface ErrorSectionFields {
    stakeholderUserGroupId: number | null;
    errorCode?: string | null;
    exceptionMessage?: string | null;
}

interface PartnerSectionFields {
    partnerIdFrom?: number | null;
    formatIdFrom?: number;
    variantIdFrom?: number;
    versionIdFrom?: number;

    partnerIdTo?: number | null;
    formatIdTo?: number;
    variantIdTo?: number;
    versionIdTo?: number;
}

interface AdvancedSectionFields {
    documentId?: string;
    interchangeNumber?: string;
    messageNumber?: string;
    displayAddress?: string;
    filename?: string;
    distributionId?: number;
    interchangeGuid?: string;
    messageGuid?: string;
    transmissionId?: string;
    documentGuid?: string;
    originatingId?: string;
    partnerSystemId?: number | null;
}

type TrackingSearchModel =
    & BaseSearchFields
    & ErrorSectionFields
    & PartnerSectionFields
    & AdvancedSectionFields;

const SECTION_KEYS = {
    error: ['stakeholderUserGroupId', 'errorCode', 'exceptionMessage'] as const,
    partner: [
        'partnerIdFrom',
        'formatIdFrom',
        'partnerIdTo',
        'formatIdTo',
    ] as const,
    advanced: [
        'documentId',
        'interchangeNumber',
        'messageNumber',
        'displayAddress',
        'filename',
        'distributionId',
        'interchangeGuid',
        'messageGuid',
        'transmissionId',
        'documentGuid',
        'originatingId',
        'partnerSystemId',
    ] as const,
} satisfies Record<Exclude<SectionKey, 'trackingFields'>, readonly (keyof TrackingSearchModel)[]>;

type SectionKey = 'error' | 'partner' | 'advanced' | 'trackingFields';
type SectionValueCounts = Record<SectionKey, number>;

type AccordionSelectionChangedEvent = Readonly<{
    component?: { option: (name: 'selectedItems') => unknown };
}>;

type AccordionItem = Readonly<{ title?: unknown }>;

type TrackingSearchState = Readonly<{
    searchObject?: Partial<TrackingSearchModel>;
    trackingFields?: DataFieldGroup[];
    openedSectionKeys?: readonly SectionKey[];
}>;

type SectionMeta = Readonly<{
    key: SectionKey;
    title: string;
}>;

const SECTION_META: readonly SectionMeta[] = [
    { key: 'error', title: 'Error' },
    { key: 'partner', title: 'Partner' },
    { key: 'advanced', title: 'Advanced Search' },
    { key: 'trackingFields', title: 'Tracking Fields' },
] as const;

const SECTION_KEY_BY_TITLE: ReadonlyMap<string, SectionKey> = new Map(
    SECTION_META.map((s) => [s.title, s.key]),
);

const SECTION_TITLE_BY_KEY: ReadonlyMap<SectionKey, string> = new Map(
    SECTION_META.map((s) => [s.key, s.title]),
);

const NON_TRACKING_KEYS = ['error', 'partner', 'advanced'] as const;
